import { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

const stripeLoaders = {};

function stripeFor(publishableKey) {
  if (!stripeLoaders[publishableKey]) {
    stripeLoaders[publishableKey] = loadStripe(publishableKey);
  }
  return stripeLoaders[publishableKey];
}

function SetupForm({ onConfirmed, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) throw confirmError;
      if (setupIntent?.status !== 'succeeded' || !setupIntent.payment_method) {
        throw new Error('Your payment method was not saved. Please try again.');
      }
      await onConfirmed(setupIntent.id, setupIntent.payment_method);
    } catch (err) {
      const message = err?.message || 'Your payment method could not be saved.';
      setError(message);
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card', 'us_bank_account'],
          wallets: { link: 'never' },
        }}
      />
      {error && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <Button type="submit" disabled={!stripe || submitting} className="w-full bg-blue-600 hover:bg-blue-700">
        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><ShieldCheck className="mr-2 h-4 w-4" />Save payment method</>}
      </Button>
    </form>
  );
}

/** Stripe-hosted fields within the portal. VyomOS never receives PAN/bank data. */
export default function PaymentMethodOnFileForm({
  clientSecret,
  publishableKey,
  onConfirmed,
  onError,
}) {
  const stripePromise = useMemo(
    () => (publishableKey ? stripeFor(publishableKey) : null),
    [publishableKey],
  );

  if (!clientSecret || !stripePromise) {
    return <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Preparing secure payment form...</div>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '8px' } },
      }}
    >
      <SetupForm onConfirmed={onConfirmed} onError={onError} />
    </Elements>
  );
}
